import React, { useEffect, useRef } from "react";
import { applyLiveFxVars, shineTiltProfile } from "./aiShine";

function clamp(value, min = 0, max = 100) {
  return Math.min(Math.max(value, min), max);
}

function round(value, precision = 3) {
  const p = 10 ** precision;
  return Math.round(value * p) / p;
}

function createSpring(initial, settings) {
  const keys = Object.keys(initial);
  const current = { ...initial };
  const velocity = Object.fromEntries(keys.map((k) => [k, 0]));
  let target = { ...initial };
  let stiffness = settings.stiffness;
  let damping = settings.damping;

  return {
    get() {
      return current;
    },
    set(next, opts = {}) {
      target = { ...target, ...next };
      if (opts.stiffness != null) stiffness = opts.stiffness;
      if (opts.damping != null) damping = opts.damping;
      if (opts.hard) {
        Object.assign(current, target);
        keys.forEach((k) => {
          velocity[k] = 0;
        });
      }
    },
    configure(next) {
      if (next.stiffness != null) stiffness = next.stiffness;
      if (next.damping != null) damping = next.damping;
    },
    tick() {
      let moving = false;
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        const force = (target[k] - current[k]) * stiffness;
        velocity[k] = (velocity[k] + force) * damping;
        current[k] += velocity[k];
        if (
          Math.abs(velocity[k]) > 0.001 ||
          Math.abs(target[k] - current[k]) > 0.01
        ) {
          moving = true;
        } else {
          current[k] = target[k];
          velocity[k] = 0;
        }
      }
      return moving;
    },
  };
}

const INTERACT = { stiffness: 0.1, damping: 0.28 };
const IDLE = { stiffness: 0.03, damping: 0.4 };

export default function AiHoloPreview({
  styles,
  className = "",
  cardClassName = "",
  cardStyle,
  foilEffect = null,
  onClose,
  children,
}) {
  const stageRef = useRef(null);
  const cardRef = useRef(null);
  const fxRef = useRef(null);
  const interactingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const rafRef = useRef(0);
  const settleTimerRef = useRef(0);
  const introRafRef = useRef(0);
  const idleRafRef = useRef(0);
  const shineAmtRef = useRef(shineTiltProfile(foilEffect));
  const springsRef = useRef(null);

  if (!springsRef.current) {
    springsRef.current = {
      rotate: createSpring({ x: 0, y: 0 }, INTERACT),
      glare: createSpring({ x: 50, y: 50, o: 0 }, INTERACT),
      translate: createSpring({ x: 0, y: 0 }, INTERACT),
      scale: createSpring({ v: 1 }, { stiffness: 0.04, damping: 0.42 }),
    };
  }

  shineAmtRef.current = shineTiltProfile(foilEffect);

  const applyStyles = () => {
    const springs = springsRef.current;
    const rotate = springs.rotate.get();
    const glare = springs.glare.get();
    const translate = springs.translate.get();
    const scale = springs.scale.get().v;
    const cardEl = cardRef.current;
    const fxEl = fxRef.current;
    const hue = 48;
    let mx = glare.x / 100;
    let my = glare.y / 100;
    if (!interactingRef.current) {
      mx = clamp(0.5 - rotate.x / 36, 0.12, 0.88);
      my = clamp(0.5 + rotate.y / 36, 0.12, 0.88);
    }
    if (cardEl) {
      cardEl.style.setProperty("--rotate-x", `${rotate.x}deg`);
      cardEl.style.setProperty("--rotate-y", `${rotate.y}deg`);
      cardEl.style.setProperty("--card-scale", String(scale));
      cardEl.style.setProperty("--translate-x", `${translate.x}px`);
      cardEl.style.setProperty("--translate-y", `${translate.y}px`);
      applyLiveFxVars(cardEl, { mx, my, hue });
    }
    if (fxEl) {
      applyLiveFxVars(fxEl, { mx, my, hue });
      const faceEl = styles.aiHoloFace
        ? fxEl.getElementsByClassName(styles.aiHoloFace)[0]
        : null;
      if (faceEl) {
        applyLiveFxVars(faceEl, { mx, my, hue, includeOpacities: false });
      }
      const foilClasses = [
        styles.foilOverlay,
        styles.aiHoloFx,
        styles.holoShine,
        styles.holoGlare,
      ].filter(Boolean);
      foilClasses.forEach((className) => {
        Array.from(fxEl.getElementsByClassName(className)).forEach((node) =>
          applyLiveFxVars(node, { mx, my, hue, includeOpacities: false }),
        );
      });
    }
  };

  const stopIdle = () => {
    if (idleRafRef.current) {
      cancelAnimationFrame(idleRafRef.current);
      idleRafRef.current = 0;
    }
  };

  const startLoop = () => {
    if (rafRef.current) return;
    const tick = () => {
      const springs = springsRef.current;
      const moving =
        springs.rotate.tick() ||
        springs.glare.tick() ||
        springs.translate.tick() ||
        springs.scale.tick();
      applyStyles();
      if (moving || interactingRef.current || idleRafRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = 0;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const startIdleWobble = () => {
    stopIdle();
    interactingRef.current = false;
    const amt = shineAmtRef.current;
    let t = Math.random() * Math.PI * 2;
    const idleTick = () => {
      if (interactingRef.current) {
        idleRafRef.current = 0;
        return;
      }
      t += amt.premium || amt.foil ? 0.034 : 0.026;
      const springs = springsRef.current;
      springs.rotate.configure(IDLE);
      springs.translate.configure(IDLE);
      const wobble = amt.premium || amt.foil ? 1.15 : 0.85;
      const rotX = Math.sin(t) * 18 * wobble;
      const rotY = Math.cos(t * 0.85) * 16 * wobble;
      springs.rotate.set({ x: rotX, y: rotY });
      springs.glare.set(
        {
          x: 50 - (rotX / 18) * 38,
          y: 50 + (rotY / 16) * 34,
          o: 1,
        },
        { hard: true },
      );
      springs.translate.set({
        x: Math.sin(t * 0.5) * 6 * wobble,
        y: Math.cos(t * 0.45) * 5 * wobble,
      });
      springs.scale.set({ v: 1 });
      startLoop();
      idleRafRef.current = requestAnimationFrame(idleTick);
    };
    idleRafRef.current = requestAnimationFrame(idleTick);
  };

  const updateFromPoint = (clientX, clientY) => {
    const stage = stageRef.current;
    const cardEl = cardRef.current;
    const fxEl = fxRef.current;
    if (!stage) return;
    const rect = (cardEl || stage).getBoundingClientRect();
    const percent = {
      x: clamp(round((100 / Math.max(1, rect.width)) * (clientX - rect.left))),
      y: clamp(round((100 / Math.max(1, rect.height)) * (clientY - rect.top))),
    };
    const center = { x: percent.x - 50, y: percent.y - 50 };
    const amt = shineAmtRef.current;
    const divisor = amt.tiltDivisor || 1.7;
    const springs = springsRef.current;
    springs.rotate.configure(INTERACT);
    springs.glare.configure(INTERACT);
    springs.translate.configure(INTERACT);
    springs.rotate.set({
      x: round(-(center.x / divisor)),
      y: round(center.y / divisor),
    });
    springs.glare.set({ x: percent.x, y: percent.y, o: 1 }, { hard: true });
    springs.translate.set({
      x: round(center.x * 0.22),
      y: round(center.y * 0.18),
    });
    springs.scale.set({ v: amt.interactScale || 1.08 });
    const mx = percent.x / 100;
    const my = percent.y / 100;
    const hue = 48;
    if (cardEl) applyLiveFxVars(cardEl, { mx, my, hue });
    if (fxEl) {
      applyLiveFxVars(fxEl, { mx, my, hue });
      const faceEl = styles.aiHoloFace
        ? fxEl.getElementsByClassName(styles.aiHoloFace)[0]
        : null;
      if (faceEl) {
        applyLiveFxVars(faceEl, { mx, my, hue, includeOpacities: false });
      }
      const foilClasses = [
        styles.foilOverlay,
        styles.aiHoloFx,
        styles.holoShine,
        styles.holoGlare,
      ].filter(Boolean);
      foilClasses.forEach((className) => {
        Array.from(fxEl.getElementsByClassName(className)).forEach((node) =>
          applyLiveFxVars(node, { mx, my, hue, includeOpacities: false }),
        );
      });
    }
    startLoop();
  };

  const settle = (delay = 160) => {
    interactingRef.current = false;
    window.clearTimeout(settleTimerRef.current);
    settleTimerRef.current = window.setTimeout(() => {
      if (interactingRef.current) return;
      startIdleWobble();
    }, delay);
  };

  useEffect(() => {
    const springs = springsRef.current;
    interactingRef.current = false;
    pointerIdRef.current = null;
    window.clearTimeout(settleTimerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (introRafRef.current) cancelAnimationFrame(introRafRef.current);
    stopIdle();
    springs.rotate.set({ x: 0, y: 0 }, { hard: true });
    springs.glare.set({ x: 42, y: 38, o: 1 }, { hard: true });
    springs.translate.set({ x: 0, y: 0 }, { hard: true });
    springs.scale.set({ v: 1 }, { hard: true });
    applyStyles();

    let t = 0;
    const introTick = () => {
      if (interactingRef.current) {
        introRafRef.current = 0;
        return;
      }
      t += 0.055;
      const amt = shineAmtRef.current;
      const boost = amt.premium || amt.foil ? 1.35 : 1;
      springs.rotate.configure({ stiffness: 0.05, damping: 0.32 });
      springs.translate.configure({ stiffness: 0.05, damping: 0.32 });
      const rotX = Math.sin(t) * 28 * boost;
      const rotY = Math.cos(t * 0.9) * 24 * boost;
      springs.rotate.set({ x: rotX, y: rotY });
      springs.glare.set(
        {
          x: 50 - (rotX / 28) * 42,
          y: 50 + (rotY / 24) * 36,
          o: 1,
        },
        { hard: true },
      );
      springs.translate.set({
        x: Math.sin(t * 0.6) * 10,
        y: Math.cos(t * 0.55) * 8,
      });
      springs.scale.set({ v: 1.03 });
      startLoop();
      if (t < Math.PI * 2) {
        introRafRef.current = requestAnimationFrame(introTick);
      } else {
        introRafRef.current = 0;
        startIdleWobble();
      }
    };
    introRafRef.current = requestAnimationFrame(introTick);

    return () => {
      if (introRafRef.current) cancelAnimationFrame(introRafRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopIdle();
      window.clearTimeout(settleTimerRef.current);
    };
  }, [foilEffect]);

  const pointOnCard = (clientX, clientY) => {
    const cardEl = cardRef.current;
    if (!cardEl) return false;
    const rect = cardEl.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  };

  // Taps anywhere close the preview; a press that moves (tilting the card)
  // does not.
  const TAP_MAX_MOVE_PX = 10;
  const tapStartRef = useRef(null);
  const tapMovedRef = useRef(false);

  const trackTapMove = (event) => {
    const start = tapStartRef.current;
    if (!start || tapMovedRef.current) return;
    if (
      Math.hypot(event.clientX - start.x, event.clientY - start.y) >
      TAP_MAX_MOVE_PX
    ) {
      tapMovedRef.current = true;
    }
  };

  const onPointerDown = (event) => {
    tapStartRef.current = { x: event.clientX, y: event.clientY };
    tapMovedRef.current = false;
    if (!pointOnCard(event.clientX, event.clientY)) return;
    event.stopPropagation();
    interactingRef.current = true;
    pointerIdRef.current = event.pointerId;
    stopIdle();
    cardRef.current?.setPointerCapture?.(event.pointerId);
    updateFromPoint(event.clientX, event.clientY);
  };

  const onPointerMove = (event) => {
    trackTapMove(event);
    if (event.pointerType === "touch" && !interactingRef.current) return;
    if (pointerIdRef.current != null) {
      if (event.pointerId !== pointerIdRef.current) return;
      event.stopPropagation();
      updateFromPoint(event.clientX, event.clientY);
      return;
    }
    if (event.pointerType === "mouse") {
      if (!pointOnCard(event.clientX, event.clientY)) {
        if (interactingRef.current) settle(160);
        return;
      }
      if (!interactingRef.current) {
        interactingRef.current = true;
        stopIdle();
      }
      event.stopPropagation();
      updateFromPoint(event.clientX, event.clientY);
    }
  };

  const onPointerUp = (event) => {
    if (
      pointerIdRef.current != null &&
      event.pointerId !== pointerIdRef.current
    ) {
      return;
    }
    event.stopPropagation();
    try {
      cardRef.current?.releasePointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
    pointerIdRef.current = null;
    settle(event.pointerType === "mouse" ? 280 : 160);
  };

  const onCardPointerLeave = (event) => {
    if (event.pointerType !== "mouse") return;
    if (pointerIdRef.current != null) return;
    settle(200);
  };

  return (
    <div
      className={`${styles.cardExamArtExpanded} ${className}`.trim()}
      onClick={(event) => {
        event.stopPropagation();
        onClose?.();
      }}
      role="presentation"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose?.();
        }
      }}
    >
      <button
        type="button"
        className={styles.aiHoloClose}
        aria-label="Close"
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        ×
      </button>
      <div
        ref={stageRef}
        className={styles.aiHoloStage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={(event) => {
          tapMovedRef.current = true;
          onPointerUp(event);
        }}
        onClick={(event) => {
          event.stopPropagation();
          if (tapMovedRef.current) return;
          onClose?.();
        }}
      >
        <div
          ref={cardRef}
          className={`${styles.aiHoloCard} ${cardClassName}`.trim()}
          style={cardStyle}
          onPointerLeave={onCardPointerLeave}
        >
          <div ref={fxRef} className={styles.aiHoloCardFx}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
